import React, { useState, useEffect, useRef, useCallback } from 'react';

import { Mail } from '@mui/icons-material';
import { Tooltip } from '@mui/material';

import { 
  Character,
  CharacterItem,
  Receivable,
  useCharacterReceivableAlerts
} from "@dndModels";

import CommonUtilities from "@utilities/common_utilities";
import CharConnectUtilities from '@dndUtilities/char_connect_utilities';

import CharacterMailInput from "@dndComponents/character/receivables/CharacterMailInput";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import DandDAPI from '@utilities/smart_api';
import ToastBox from '@components/display/ToastBox';
import FlexBox from '@components/layout/FlexBox';

interface Props {
  char: Character;
  disabled: boolean;
}

const common_util = CommonUtilities.getInstance();
const api = DandDAPI.getInstance();
const connect_util = CharConnectUtilities.getInstance();

export default function CharacterMailButton({
  char,
  disabled,
}: Props) {
  useCharacterReceivableAlerts(char._id, async (receivable) => {
    // swap this for your actual toast call
    // showToast(`${receivable.from_id} sent: ${receivable.message}`);
    console.log(receivable);
    const processed_group = await api.processResponse2<Receivable>({ all: [receivable], data_type: "receivable" });
    // This receivable should belong to char, but double check just in case.
    if (processed_group.length === 1 && char.campaign) {
      const processed = processed_group[0];
      // console.log(processed);
      if (processed.char_id === char._id) {
        if (!char.receivables.some(o => o._id === processed._id)) { 
          // This hook can get triggered multiple times for the same receivable. 
          // We only want one to trigger it.
          char.receivables.push(processed);
          for (const item of processed.items) {
            if (item.obj instanceof CharacterItem) {
              await connect_util.connectItem(char, item.obj);
            }
          }
          setFlashing(true);
          if (processed.from_id === "DM") {
            setToastMsg(`Mail Received from Game Master.`);
          } else {
            const from_char = char.campaign.characters.find(o => o._id === processed.from_id);
            if (from_char) {
              setToastMsg(`Mail Received from ${from_char.name}.`);
            } else {
              setToastMsg(`Mail Received from Unknown.`);
            }
          }
          setToastTime(common_util.get_time_number());
        }
      }
    }
  });
  const [fetched, setFetched] = useState(false);
  const [do_recalc, setDoRecalc] = useState(false);
  const [flashing, setFlashing] = useState((
    char.receivables.filter(o => o.received_dt === 0).length > 0
  ));
  const [open, setOpen] = useState(false);
  const [toast_time, setToastTime] = useState(-1);
  const [toast_msg, setToastMsg] = useState("");
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalc = useCallback(() => {
    if (char.receivables.filter(o => o.received_dt === 0).length > 0) {
      setFlashing(true);
    } else {
      setFlashing(false);
    }
  }, [char]);

  useEffect(() => {
    if (do_recalc) {
      setDoRecalc(false);
      recalc();
    }
  }, [do_recalc, recalc]);

  const receivablesChanged = useCallback(() => {
    setDoRecalc(true);
  }, []);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);

      char.subscription_holder.subscribe("Receivables", mounted, receivablesChanged);
    }
  }, [fetched, char]);

  const renderExtras = () => {
    return (
      <>
        <ModalOrDrawer 
          open={ open } 
          full_page
          onClose={() => {
            setOpen(false); 
            char.subscription_holder.modalClosed();
          }}>
          { open &&
            <FlexBox style={{ maxHeight: window.innerHeight - 34 }}>
              <CharacterMailInput
                char={char}
              />
            </FlexBox>
          }
        </ModalOrDrawer>
        <ToastBox
          openTime={ toast_time }
        >
          { toast_msg }
        </ToastBox>
      </>
    );
  }

  return (
    <>
      <Tooltip title="Mail">
        <Mail className={ flashing ? "Flashing clickable" : "clickable" }
          onClick={() => {
            if (!disabled) {
              setOpen(true);
            }
          }}
        />
      </Tooltip>
      { renderExtras() }
    </>
  ); 
}
